import React, { useState, useEffect } from 'react';
import { User } from '../../services/authService';
import { getAllDrawings, deleteDrawingById, Drawing } from '../../services/drawingService';
import './PastDrawings.css';

interface PastDrawingsProps {
  user: User | null;
  onLoadDrawing: (drawing: Drawing) => void;
  onClose: () => void;
}

const PastDrawings: React.FC<PastDrawingsProps> = ({ user, onLoadDrawing, onClose }) => {
  const [drawings, setDrawings] = useState<Drawing[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getUserId = (): number => {
    return user?.id || 0;
  };

  const loadDrawings = async () => {
    const userId = getUserId();
    if (!userId) return;

    setIsLoading(true);
    setError(null);
    try {
      const allDrawings = await getAllDrawings(userId);
      setDrawings(allDrawings);
    } catch (err) {
      console.error('Failed to load drawings:', err);
      setError('Failed to load drawings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDrawings();
  }, [user]);

  const handleLoad = async (drawing: Drawing) => {
    if (window.confirm('Load this drawing? Your current drawing will be replaced.')) {
      onLoadDrawing(drawing);
      onClose();
    }
  };

  const handleDelete = async (drawingId: number) => {
    if (!window.confirm('Are you sure you want to delete this drawing? This cannot be undone.')) {
      return;
    }

    const userId = getUserId();
    if (!userId) return;

    try {
      await deleteDrawingById(userId, drawingId);
      setDrawings(drawings.filter(d => d.id !== drawingId));
    } catch (err) {
      console.error('Failed to delete drawing:', err);
      alert('Failed to delete drawing');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="past-drawings-overlay" onClick={onClose}>
      <div className="past-drawings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="past-drawings-header">
          <h2 className="past-drawings-title">Past Drawings</h2>
          <button className="past-drawings-close" onClick={onClose} title="Close">
            ×
          </button>
        </div>

        {isLoading && (
          <div className="past-drawings-loading">
            <div className="past-drawings-loading-text">Loading...</div>
          </div>
        )}

        {error && (
          <div className="past-drawings-error">
            {error}
          </div>
        )}

        {!isLoading && !error && drawings.length === 0 && (
          <div className="past-drawings-empty">
            <div className="past-drawings-empty-text">No past drawings found</div>
          </div>
        )}

        {!isLoading && !error && drawings.length > 0 && (
          <div className="past-drawings-list">
            {drawings.map((drawing) => (
              <div key={drawing.id} className="past-drawings-item">
                <div className="past-drawings-thumbnail-wrapper">
                  <img
                    src={drawing.canvas_data}
                    alt={`Drawing from ${formatDate(drawing.updated_at)}`}
                    className="past-drawings-thumbnail"
                  />
                </div>
                <div className="past-drawings-info">
                  <div className="past-drawings-date">{formatDate(drawing.updated_at)}</div>
                  <div className="past-drawings-actions">
                    <button
                      className="past-drawings-action-btn past-drawings-load-btn"
                      onClick={() => handleLoad(drawing)}
                      title="Load this drawing"
                    >
                      Load
                    </button>
                    <button
                      className="past-drawings-action-btn past-drawings-delete-btn"
                      onClick={() => handleDelete(drawing.id)}
                      title="Delete this drawing"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PastDrawings;

